import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import Modal from '../../components/Modal';
import { BrandSpinnerRing } from '../../components/SiteBrandedLoader';
import { articlesService, ArticleCategoryAdmin } from '../../services/api';
import BlogSectionNav from './BlogSectionNav';
import ConfirmDialog from './ConfirmDialog';
import { apiErrorMessage, blogSiteBaseUrl, faNumber, slugify } from './blogAdmin';

interface CategoryForm {
  name: string;
  slug: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  isActive: boolean;
}

const emptyForm: CategoryForm = { name: '', slug: '', description: '', seoTitle: '', seoDescription: '', isActive: true };
const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100';

/** مدیریت دسته‌بندی‌های وبلاگ: ایجاد، ویرایش، فعال/غیرفعال، ترتیب و حذف */
const BlogCategories: React.FC = () => {
  const [categories, setCategories] = useState<ArticleCategoryAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [editing, setEditing] = useState<ArticleCategoryAdmin | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [toDelete, setToDelete] = useState<ArticleCategoryAdmin | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCategories(await articlesService.getManageCategories());
    } catch (err) {
      setError(apiErrorMessage(err, 'خطا در دریافت دسته‌ها'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (c: ArticleCategoryAdmin) => {
    setEditing(c);
    setForm({
      name: c.name,
      slug: c.slug,
      description: c.description || '',
      seoTitle: c.seoTitle || '',
      seoDescription: c.seoDescription || '',
      isActive: c.isActive,
    });
    setSlugTouched(true);
    setFormError('');
    setModalOpen(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError('نام دسته را وارد کنید.');
      return;
    }
    setSaving(true);
    setFormError('');
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || slugify(form.name),
      description: form.description.trim(),
      seoTitle: form.seoTitle.trim(),
      seoDescription: form.seoDescription.trim(),
      isActive: form.isActive,
    };
    try {
      if (editing) await articlesService.updateCategory(editing.id, payload);
      else await articlesService.createCategory(payload);
      setModalOpen(false);
      showToast(editing ? 'دسته ویرایش شد' : 'دسته ساخته شد');
      load();
    } catch (err) {
      setFormError(apiErrorMessage(err, 'ذخیرهٔ دسته ناموفق بود'));
    } finally {
      setSaving(false);
    }
  };

  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    try {
      setCategories(await articlesService.reorderCategories(next.map((c) => c.id)));
    } catch (err) {
      showToast(apiErrorMessage(err, 'تغییر ترتیب انجام نشد'));
      load();
    }
  };

  const toggleActive = async (c: ArticleCategoryAdmin) => {
    try {
      await articlesService.updateCategory(c.id, { isActive: !c.isActive });
      showToast(c.isActive ? 'دسته غیرفعال شد و در سایت پنهان است' : 'دسته فعال شد');
      load();
    } catch (err) {
      showToast(apiErrorMessage(err, 'تغییر وضعیت انجام نشد'));
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await articlesService.deleteCategory(toDelete.id);
      showToast('دسته حذف شد');
      setToDelete(null);
      load();
    } catch (err) {
      showToast(apiErrorMessage(err, 'حذف انجام نشد'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      <BlogSectionNav
        title="دسته‌بندی‌های وبلاگ"
        description="چیپ‌های بالای صفحهٔ وبلاگ به همین ترتیب نمایش داده می‌شوند؛ دستهٔ غیرفعال در سایت دیده نمی‌شود."
        action={
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-xl bg-[#4a2e68] px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-[#392352]">
            <Plus className="h-4 w-4" aria-hidden />
            دستهٔ جدید
          </button>
        }
      />

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <BrandSpinnerRing size="md" />
          </div>
        ) : categories.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-600">هنوز دسته‌ای ساخته نشده است.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {categories.map((c, index) => (
              <li key={c.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label="بالاتر">
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === categories.length - 1} className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label="پایین‌تر">
                    <ArrowDown className="h-4 w-4" aria-hidden />
                  </button>
                </div>
                <div className="min-w-[200px] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{c.name}</span>
                    {!c.isActive && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-600">غیرفعال</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500" dir="ltr">
                    {c.slug}
                  </div>
                  {c.description && <p className="mt-1 text-xs text-gray-600">{c.description}</p>}
                </div>
                <Link to={`/articles?category=${c.id}`} className="text-xs font-bold text-gray-700 hover:text-[#4a2e68]">
                  {faNumber(c.articleCount)} مقاله ({faNumber(c.publishedCount)} منتشرشده)
                </Link>
                <label className="inline-flex items-center gap-2 text-xs font-bold text-gray-700">
                  <input type="checkbox" checked={c.isActive} onChange={() => toggleActive(c)} className="h-4 w-4 accent-[#4a2e68]" />
                  فعال
                </label>
                <div className="flex gap-1">
                  <a
                    href={`${blogSiteBaseUrl()}/articles?category=${encodeURIComponent(c.slug)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg p-2 text-gray-600 hover:bg-violet-50"
                    title="مشاهده در سایت"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </a>
                  <button type="button" onClick={() => openEdit(c)} className="rounded-lg p-2 text-gray-600 hover:bg-violet-50" title="ویرایش">
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                  <button type="button" onClick={() => setToDelete(c)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="حذف">
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={() => !saving && setModalOpen(false)} title={editing ? 'ویرایش دسته' : 'دستهٔ جدید'} size="medium">
        <form onSubmit={save} className="space-y-4">
          {formError && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{formError}</div>}
          <label className="block space-y-1.5">
            <span className="text-sm font-bold text-gray-800">نام دسته *</span>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: slugTouched ? f.slug : slugify(e.target.value) }))}
              className={inputClass}
              autoFocus
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-bold text-gray-800">نامک (slug)</span>
            <input
              value={form.slug}
              dir="auto"
              onChange={(e) => {
                setSlugTouched(true);
                setForm((f) => ({ ...f, slug: e.target.value }));
              }}
              className={`${inputClass} font-mono`}
            />
            <span className="block text-xs text-gray-500">در آدرس فیلتر وبلاگ استفاده می‌شود؛ مثلاً ‎/articles?category=khodshenasi</span>
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-bold text-gray-800">توضیح کوتاه</span>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={inputClass} />
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-sm font-bold text-gray-800">عنوان سئو</span>
              <input value={form.seoTitle} onChange={(e) => setForm((f) => ({ ...f, seoTitle: e.target.value }))} className={inputClass} />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-bold text-gray-800">توضیحات سئو</span>
              <input value={form.seoDescription} onChange={(e) => setForm((f) => ({ ...f, seoDescription: e.target.value }))} className={inputClass} />
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-800">
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 accent-[#4a2e68]" />
            فعال (نمایش در سایت)
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700">
              انصراف
            </button>
            <button type="submit" disabled={saving} className="rounded-xl bg-[#4a2e68] px-4 py-2 text-sm font-bold text-white hover:bg-[#392352] disabled:opacity-60">
              {saving ? 'در حال ذخیره…' : 'ذخیره'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        danger
        busy={deleting}
        title="حذف دسته"
        confirmLabel="حذف"
        message={
          toDelete && toDelete.articleCount > 0 ? (
            <>
              دستهٔ «{toDelete.name}» {faNumber(toDelete.articleCount)} مقاله دارد و حذف نمی‌شود. ابتدا مقالات را به دستهٔ دیگری منتقل کنید یا دسته را غیرفعال کنید.
            </>
          ) : (
            <>دستهٔ «{toDelete?.name}» حذف شود؟</>
          )
        }
        onConfirm={doDelete}
        onClose={() => setToDelete(null)}
      />

      {toast && (
        <div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default BlogCategories;
